
import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/90'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection('home')}>
            <img src="https://public.readdy.ai/ai/img_res/ddda166c-733e-4fd1-919c-ed4cd7295b45.png" alt="Vera House Logo" className="h-12 w-auto" />
            <span className="text-2xl font-bold text-[#2C5F3F]" style={{ fontFamily: 'Georgia, serif' }}>Vera House</span>
          </div>
          <div className="flex items-center gap-8">
            <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-[#7BA882] transition-colors cursor-pointer">Home</button>
            <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-[#7BA882] transition-colors cursor-pointer">About</button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-[#7BA882] transition-colors cursor-pointer">Contact</button>
            <a href="mailto:patrick@verahouse501.com" className="px-6 py-2 bg-[#7BA882] text-white rounded-full hover:bg-[#6A9771] transition-colors whitespace-nowrap cursor-pointer">Get in Touch</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-sm uppercase tracking-wider text-gray-500">Est. 1985 • Birmingham, AL</p>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">Creating Safe Homes for Our Community</h1>
              <p className="text-xl text-gray-600 leading-relaxed">Providing quality housing for mentally handicapped individuals under HUD Section 202 program</p>
              <div className="flex gap-4 pt-4">
                <button onClick={() => scrollToSection('about')} className="px-8 py-3 bg-[#7BA882] text-white rounded-full hover:bg-[#6A9771] transition-colors whitespace-nowrap cursor-pointer">Learn More</button>
                <button onClick={() => scrollToSection('contact')} className="px-8 py-3 border-2 border-[#7BA882] text-[#7BA882] rounded-full hover:bg-[#7BA882] hover:text-white transition-colors whitespace-nowrap cursor-pointer">Contact Us</button>
              </div>
            </div>
            <div className="relative h-96 lg:h-[500px]">
              <img src="https://public.readdy.ai/ai/img_res/9befaaee823de12a3ef78a92684fc8ee.jpg" alt="Community Home Illustration" className="w-full h-full object-cover rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-4 mb-12">
            <p className="text-sm uppercase tracking-wider text-[#7BA882] font-semibold">Our Mission</p>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 max-w-3xl">Supporting Independence Through Quality Housing</h2>
            <p className="text-xl text-gray-600 max-w-3xl">Vera House has been dedicated to providing safe, comfortable housing solutions for mentally handicapped individuals in Birmingham since 1985</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-12">
            <div className="lg:col-span-2 h-80">
              <img src="https://readdy.ai/api/search-image?query=abstract%20warm%20interior%20home%20illustration%20with%20soft%20lighting%20cozy%20furniture%20and%20welcoming%20atmosphere%20in%20pastel%20beige%20cream%20and%20sage%20green%20tones%20minimalist%20modern%20design%20suggesting%20comfort%20safety%20and%20independence&width=400&height=320&seq=about-interior&orientation=landscape" alt="Safe Living Space" className="w-full h-full object-cover rounded-3xl" />
            </div>
            <div className="lg:col-span-3 h-80">
              <img src="https://readdy.ai/api/search-image?query=abstract%20community%20illustration%20showing%20diverse%20people%20connecting%20and%20supporting%20each%20other%20in%20warm%20pastel%20colors%20with%20geometric%20shapes%20suggesting%20unity%20belonging%20and%20care%20modern%20minimalist%20design%20with%20soft%20gradients&width=600&height=320&seq=about-community&orientation=landscape" alt="Community Support" className="w-full h-full object-cover rounded-3xl" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm">
              <div className="w-12 h-12 bg-[#7BA882] rounded-full flex items-center justify-center mb-4">
                <i className="ri-home-heart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">10</h3>
              <p className="text-gray-600">Bedroom Units</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm">
              <div className="w-12 h-12 bg-[#7BA882] rounded-full flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">HUD</h3>
              <p className="text-gray-600">Section 202 Certified</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm">
              <div className="w-12 h-12 bg-[#7BA882] rounded-full flex items-center justify-center mb-4">
                <i className="ri-community-line text-2xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">35+</h3>
              <p className="text-gray-600">Years of Service</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-start mb-12">
            <div className="space-y-4">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">What We <span className="text-[#7BA882]">Offer</span></h2>
              <p className="text-xl text-gray-600 max-w-2xl">Comprehensive housing solutions designed for comfort and independence</p>
            </div>
            <div className="px-4 py-2 bg-gray-900 text-white rounded-full text-sm whitespace-nowrap">HUD Certified</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer">
              <div className="h-32 bg-[#7BA882] flex items-center justify-center">
                <i className="ri-home-smile-line text-5xl text-white"></i>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Safe Living Spaces</h3>
                <p className="text-gray-600">Comfortable and secure housing designed for independent living</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer">
              <div className="h-32 bg-[#7BA882] flex items-center justify-center">
                <i className="ri-24-hours-line text-5xl text-white"></i>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">24/7 Support</h3>
                <p className="text-gray-600">Round-the-clock assistance to ensure resident wellbeing</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer">
              <div className="h-32 bg-[#7BA882] flex items-center justify-center">
                <i className="ri-group-line text-5xl text-white"></i>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Community Activities</h3>
                <p className="text-gray-600">Engaging programs that foster connection and growth</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer">
              <div className="h-32 bg-[#7BA882] flex items-center justify-center">
                <i className="ri-wheelchair-line text-5xl text-white"></i>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Accessible Design</h3>
                <p className="text-gray-600">Fully accessible facilities meeting all ADA requirements</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://readdy.ai/api/search-image?query=soft%20watercolor%20illustration%20of%20Birmingham%20Alabama%20skyline%20in%20muted%20pastel%20colors%20with%20gentle%20clouds%20and%20warm%20sunset%20tones%20abstract%20artistic%20style%20with%20minimal%20details%20creating%20peaceful%20welcoming%20atmosphere&width=1400&height=400&seq=cta-background&orientation=landscape" alt="Birmingham Skyline" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8">
            <div className="space-y-6">
              <h2 className="text-5xl lg:text-7xl font-bold text-white leading-tight">Ready to Learn More?</h2>
              <button onClick={() => scrollToSection('contact')} className="px-8 py-4 bg-white text-gray-900 rounded-full hover:bg-gray-100 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <span className="font-semibold">Get in Touch</span>
                <i className="ri-arrow-right-circle-line text-xl"></i>
              </button>
            </div>
            <div className="text-white text-xl max-w-md">
              <p>Contact us today to discuss housing options</p>
              <p className="text-white/80">We're here to help</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact/Footer Section */}
      <section id="contact" className="py-20 px-6 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="bg-black rounded-3xl p-12 lg:p-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
              {/* Left Column - Contact Form */}
              <div className="lg:col-span-5 space-y-6">
                <div>
                  <h3 className="text-4xl font-light text-white mb-2">Connect With Us</h3>
                  <p className="text-gray-400">We're here to answer your questions</p>
                </div>
                <div className="space-y-4">
                  <input type="email" placeholder="Your email address" className="w-full bg-transparent border-b border-gray-700 text-white py-3 focus:border-[#7BA882] outline-none transition-colors text-base" />
                  <textarea placeholder="Your message" rows={4} className="w-full bg-transparent border-b border-gray-700 text-white py-3 focus:border-[#7BA882] outline-none transition-colors resize-none text-base"></textarea>
                  <button className="px-8 py-3 bg-white text-gray-900 rounded-full hover:bg-gray-100 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                    <span className="font-semibold">Send Message</span>
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>

              {/* Middle Column - Quick Links */}
              <div className="lg:col-span-3 space-y-4">
                <h4 className="text-white font-semibold mb-6">Navigation</h4>
                <button onClick={() => scrollToSection('home')} className="block text-gray-400 hover:text-white transition-colors underline cursor-pointer text-left">Home</button>
                <button onClick={() => scrollToSection('about')} className="block text-gray-400 hover:text-white transition-colors underline cursor-pointer text-left">About</button>
                <button onClick={() => scrollToSection('contact')} className="block text-gray-400 hover:text-white transition-colors underline cursor-pointer text-left">Contact</button>
              </div>

              {/* Right Column - Contact Info */}
              <div className="lg:col-span-4 space-y-6">
                <h4 className="text-white font-semibold mb-6">Reach Us</h4>
                <div className="space-y-4">
                  <a href="tel:6308771111" className="block text-2xl text-white hover:text-[#7BA882] transition-colors cursor-pointer">(630) 877-1111</a>
                  <a href="mailto:patrick@verahouse501.com" className="block text-gray-400 hover:text-white transition-colors cursor-pointer">patrick@verahouse501.com</a>
                </div>
                <div className="pt-4">
                  <h5 className="text-white font-semibold mb-3">Visit Us</h5>
                  <p className="text-gray-400 leading-relaxed">940 Montclair Rd<br />Birmingham, AL 35213<br />United States</p>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="pt-12 border-t border-gray-800 flex flex-col lg:flex-row justify-between items-center gap-6">
              <div className="flex gap-6">
                <a href="#" className="text-gray-400 hover:text-white transition-colors underline cursor-pointer">LinkedIn</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors underline cursor-pointer">Facebook</a>
              </div>
              <p className="text-gray-500 text-sm">© 2025 Vera House. All rights reserved.</p>
              <div className="flex gap-6">
                <a href="#" className="text-gray-400 hover:text-white transition-colors underline cursor-pointer">Privacy Policy</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors underline cursor-pointer">Terms</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
